/**
 * OnboardingWizard — Phase 3 (Drive-First Pivot).
 *
 * Shown after first login when the user owns a workspace, hasn't connected
 * Drive, and has no subscription yet. The wizard has two options:
 *
 *   1. Connect Google Drive — starts 90-day free trial
 *   2. Skip for now         — user can still join other workspaces, but can't
 *                             upload to their own until they connect Drive
 *
 * Dismissal is tracked in localStorage ('ovacs_onboarding_dismissed') so the
 * wizard doesn't re-appear constantly. The backend audit log captures the
 * "skip" choice via POST /api/onboarding/skip/.
 */

import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  HiOutlineCloud,
  HiOutlineCheckCircle,
  HiOutlineArrowRight,
} from 'react-icons/hi2'
import { usePricingMode } from '../../contexts/PricingModeContext'
import { useAuth } from '../../contexts/AuthContext'
import { onboardingAPI } from '../../services/subscription'
import { googleDriveAPI } from '../../services/integrations/googleDrive'

// Per-user dismissal so a previous tester's "Skip" doesn't suppress
// the wizard for a new signup in the same browser.
const dismissKeyFor = (userId) => `ovacs_onboarding_dismissed_${userId || 'anon'}`

// Routes where the wizard should NEVER show (pre-auth pages)
const PRE_AUTH_PATHS = ['/login', '/register', '/verify-otp', '/forgot-password', '/reset-password', '/subscribe/mock-pay', '/subscribe/success', '/oauth/google/callback']

export default function OnboardingWizard() {
  const { t } = useTranslation()
  const { isDriveFirst, config, loading: configLoading } = usePricingMode()
  const { user, isAuthenticated } = useAuth()
  const location = useLocation()

  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [connecting, setConnecting] = useState(false)
  const [skipping, setSkipping] = useState(false)
  const [forceClosed, setForceClosed] = useState(false)

  const loadStatus = useCallback(async () => {
    try {
      const res = await onboardingAPI.status()
      const payload = res?.data?.data ?? res?.data ?? null
      setStatus(payload)
    } catch (e) {
      setStatus(null)
    } finally {
      setLoading(false)
    }
  }, [])

  // Re-fetch status whenever user becomes authenticated (or changes).
  // This is the fix for "wizard doesn't appear after signup + OTP" —
  // previously the effect ran once before auth and never re-ran.
  useEffect(() => {
    if (configLoading || !isDriveFirst) {
      setLoading(false)
      return
    }
    if (!isAuthenticated || !user) {
      // Wait for auth before hitting the protected endpoint
      setStatus(null)
      setLoading(false)
      return
    }
    setLoading(true)
    loadStatus()
  }, [configLoading, isDriveFirst, isAuthenticated, user?.id, loadStatus])

  // Reset forceClosed if the user changes (new signup on same browser)
  useEffect(() => {
    setForceClosed(false)
  }, [user?.id])

  // Don't show on pre-auth pages OR while user is unauthenticated
  const onPreAuthPath = PRE_AUTH_PATHS.some(p => location.pathname.startsWith(p))

  // Decision: show wizard?
  const dismissed = typeof window !== 'undefined'
    && user?.id
    && localStorage.getItem(dismissKeyFor(user.id)) === '1'
  // Superadmins manage the platform and don't own a customer workspace
  // in the same sense as regular users — never prompt them to connect Drive.
  const isSuperAdmin = user?.role === 'superadmin'
  const shouldShow =
    isDriveFirst &&
    !configLoading &&
    !loading &&
    !forceClosed &&
    !dismissed &&
    isAuthenticated &&
    !isSuperAdmin &&
    !onPreAuthPath &&
    status?.needs_attention === true

  if (!shouldShow) return null

  const handleConnect = async () => {
    setConnecting(true)
    try {
      const res = await googleDriveAPI.getAuthUrl('web')
      const url = res?.data?.data?.auth_url ?? res?.data?.auth_url
      if (url) {
        // Mark "in progress" — they're heading to Google
        if (user?.id) localStorage.setItem(dismissKeyFor(user.id), '1')
        window.location.href = url
        return
      }
    } catch (e) {
      // Fall through
    }
    setConnecting(false)
    alert(t('onboarding.wizard.connectError'))
  }

  const handleSkip = async () => {
    setSkipping(true)
    try { await onboardingAPI.skip() } catch (e) { /* non-fatal */ }
    if (user?.id) localStorage.setItem(dismissKeyFor(user.id), '1')
    setForceClosed(true)
    setSkipping(false)
  }

  const annualPrice = config?.annual_price_usd || '60'
  const trialDays = config?.trial_days || 90

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ow-title"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 sm:p-10 my-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 items-center justify-center mb-4">
            <HiOutlineCloud className="w-8 h-8 text-white" />
          </div>
          <h2 id="ow-title" className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            {t('onboarding.wizard.title')}
          </h2>
          <p className="text-gray-600 text-base sm:text-lg">
            {t('onboarding.wizard.subtitle')}
          </p>
        </div>

        {/* Two-card choice */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Connect Drive — featured */}
          <button
            type="button"
            onClick={handleConnect}
            disabled={connecting || skipping}
            className="text-start border-2 border-cyan-500 rounded-xl p-5 bg-gradient-to-br from-cyan-50 to-blue-50 hover:shadow-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed group"
          >
            <div className="flex items-start justify-between mb-3">
              <HiOutlineCloud className="w-7 h-7 text-cyan-600" />
              <span className="text-[10px] font-bold uppercase tracking-wider bg-cyan-600 text-white px-2 py-0.5 rounded-full">
                {t('onboarding.wizard.recommended')}
              </span>
            </div>
            <h3 className="font-bold text-gray-900 text-lg mb-1.5">
              {t('onboarding.wizard.connectTitle')}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('onboarding.wizard.connectDesc')}
            </p>
            <ul className="space-y-1.5 text-xs text-gray-700 mb-5">
              <li className="flex items-center gap-1.5">
                <HiOutlineCheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                <span>{t('onboarding.wizard.benefitTrial', { days: trialDays })}</span>
              </li>
              <li className="flex items-center gap-1.5">
                <HiOutlineCheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                <span>{t('onboarding.wizard.benefitPrice', { price: annualPrice })}</span>
              </li>
              <li className="flex items-center gap-1.5">
                <HiOutlineCheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                <span>{t('onboarding.wizard.benefitStorage')}</span>
              </li>
            </ul>
            <div className="text-sm font-semibold text-cyan-700 flex items-center gap-1 group-hover:gap-2 transition-all">
              {connecting ? t('onboarding.wizard.connectOpening') : (<>{t('onboarding.wizard.connectCta')} <HiOutlineArrowRight className="w-4 h-4 rtl:rotate-180" /></>)}
            </div>
          </button>

          {/* Skip for now */}
          <button
            type="button"
            onClick={handleSkip}
            disabled={connecting || skipping}
            className="text-start border-2 border-gray-200 rounded-xl p-5 hover:border-gray-300 hover:shadow-md transition-all disabled:opacity-60 disabled:cursor-not-allowed group"
          >
            <HiOutlineArrowRight className="w-7 h-7 text-gray-500 mb-3 rtl:rotate-180" />
            <h3 className="font-bold text-gray-900 text-lg mb-1.5">
              {t('onboarding.wizard.skipTitle')}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('onboarding.wizard.skipDesc')}
            </p>
            <ul className="space-y-1.5 text-xs text-gray-700 mb-5">
              <li className="flex items-start gap-1.5">
                <HiOutlineCheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span>{t('onboarding.wizard.skipBenefit1')}</span>
              </li>
              <li className="flex items-start gap-1.5">
                <HiOutlineCheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <span>{t('onboarding.wizard.skipBenefit2')}</span>
              </li>
              <li className="flex items-start gap-1.5">
                <span className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5">•</span>
                <span className="text-gray-500">
                  {t('onboarding.wizard.skipLimit')}
                </span>
              </li>
            </ul>
            <div className="text-sm font-medium text-gray-600 group-hover:text-gray-800">
              {skipping ? t('onboarding.wizard.skipping') : t('onboarding.wizard.skipCta')}
            </div>
          </button>
        </div>

        {/* Footer note */}
        <p className="text-center text-xs text-gray-500">
          {t('onboarding.wizard.footerNote')}
        </p>
      </div>
    </div>
  )
}
